/**
 * Alerts Page API
 * 
 * API functions for the Alerts management page.
 * Handles fetching user alerts and interacting with the alert-creation agent.
 */

import { getMerchantId, ReparoApi } from '@api';

export { getMerchantId, clearMerchantIdCache } from '@api';

// =============================================================================
// TYPES
// =============================================================================

export type GeneratedBy = 'SYSTEM' | 'USER';

export interface UserAlert {
    alertId: string;
    merchantId: string;
    alertType: string;
    alertLabel: string | null;
    config: Record<string, unknown>;
    summaryDescription: string | null;
    context: Record<string, unknown> | null;
    generatedBy: GeneratedBy;
    createdAt: string;
    updatedAt: string;
}

export interface AlertWithCounts extends UserAlert {
    totalChecklistItems: number;
    pendingChecklistItems: number;
    hasOverduePending: boolean;
}

export interface AlertCreationResponse {
    success: boolean;
    message: string;
    threadId: string;
    resourceId?: string;
    requiresClarification?: boolean;
    clarificationQuestion?: string;
    alertCreated?: {
        alertId?: string;
        alertLabel: string;
        lhs: string;
        rhs: string;
    };
    error?: string;
}

// =============================================================================
// PREDEFINED ALERT LABELS
// =============================================================================

export const PREDEFINED_ALERT_LABELS = [
    { value: 'low-stock', label: 'Low Stock' },
    { value: 'new-po', label: 'New PO' },
    { value: 'forecast-variance', label: 'Forecast Variance' },
    { value: 'over-stock', label: 'Over Stock' },
];

// =============================================================================
// USER ALERTS API
// =============================================================================

/**
 * Fetch all user alerts for the merchant
 */
export const fetchUserAlerts = async (): Promise<UserAlert[]> => {
    const merchantId = await getMerchantId();
    if (!merchantId) {
        console.error('Cannot fetch alerts: no merchant ID');
        return [];
    }

    try {
        return await ReparoApi<UserAlert[]>({
            method: 'GET',
            url: `/user-alerts?merchant_id=${encodeURIComponent(merchantId)}`,

        });
    } catch (error) {
        console.error('Error fetching alerts:', error);
        return [];
    }
};

/**
 * Get checklist item counts for an alert
 * Note: This requires a backend endpoint that returns counts
 * For now, returns placeholder data
 */
export const getChecklistItemCounts = async (alertId: string): Promise<{
    total: number;
    pending: number;
    hasOverduePending: boolean;
}> => {
    const merchantId = await getMerchantId();
    if (!merchantId) {
        return { total: 0, pending: 0, hasOverduePending: false };
    }

    try {
        // TODO: Replace with actual endpoint when available
        // Expected endpoint: GET /api/v1/check-list-items/count?alert_id=xxx&merchant_id=xxx
        const items = await ReparoApi<Array<{ alertId?: string; status?: string; alertActiveUntil?: string }>>({
            method: 'GET',
            url: `/check-list-items/list?merchant_id=${encodeURIComponent(merchantId)}`,

        });
        const alertItems = items.filter((item) => item.alertId === alertId);
        const pendingItems = alertItems.filter((item) => item.status === 'CREATED' || item.status === 'SEEN');
        const now = new Date();
        const hasOverduePending = pendingItems.some((item) => {
            if (!item.alertActiveUntil) return false;
            return new Date(item.alertActiveUntil) < now;
        });

        return {
            total: alertItems.length,
            pending: pendingItems.length,
            hasOverduePending,
        };
    } catch (error) {
        console.error('Error fetching checklist counts:', error);
        return { total: 0, pending: 0, hasOverduePending: false };
    }
};

/**
 * Fetch alerts with their checklist counts
 * Only returns USER-generated alerts (not SYSTEM-generated)
 */
export const fetchAlertsWithCounts = async (): Promise<AlertWithCounts[]> => {
    const alerts = await fetchUserAlerts();
    
    // Filter to only show USER-generated alerts
    const userAlerts = alerts.filter(alert => alert.generatedBy === 'USER');
    
    // Fetch counts for each alert
    const alertsWithCounts = await Promise.all(
        userAlerts.map(async (alert) => {
            const counts = await getChecklistItemCounts(alert.alertId);
            return {
                ...alert,
                totalChecklistItems: counts.total,
                pendingChecklistItems: counts.pending,
                hasOverduePending: counts.hasOverduePending,
            };
        })
    );

    // Sort by createdAt DESC
    return alertsWithCounts.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
};

// =============================================================================
// ALERT CREATION AGENT API
// =============================================================================

/**
 * Send a message to the alert creation agent
 */
export const sendAlertCreationMessage = async (params: {
    query: string;
    alertLabel?: string;
    threadId?: string;
    contextLimit?: number;
}): Promise<AlertCreationResponse> => {
    try {
        return await ReparoApi({
            method: 'POST',
            url: '/agent/alert-creation',
            body: JSON.stringify({
                query: params.query,
                alertLabel: params.alertLabel,
                threadId: params.threadId,
                contextLimit: params.contextLimit ?? 10,
            }),
        }) as unknown as AlertCreationResponse;
    } catch (error) {
        console.error('Error calling alert creation agent:', error);
        return {
            success: false,
            message: 'Network error',
            threadId: params.threadId || '',
            error: error instanceof Error ? error.message : 'Unknown error',
        };
    }
};

/**
 * Delete a user alert by id.
 * Returns `{ ok: true }` on success, or `{ ok: false, message }` with the
 * backend's reason on failure.
 */
export const deleteUserAlert = async (
    alertId: string,
): Promise<{ ok: true } | { ok: false; message: string }> => {
    const merchantId = await getMerchantId();
    if (!merchantId) {
        return { ok: false, message: 'No merchant ID available. Please sign in again.' };
    }

    try {
        await ReparoApi({
            method: 'DELETE',
            url: `/user-alerts/${encodeURIComponent(alertId)}?merchant_id=${encodeURIComponent(merchantId)}`,
        });
        return { ok: true };
    } catch (error: unknown) {
        console.error('Error deleting alert:', error);
        // Unwrap the nested ApiError shape produced by ReparoApi's handleError.
        const e = error as {
            error?: {
                message?: string;
                statusCode?: number;
                data?: { statusCode?: number; message?: string | string[]; error?: string };
            };
            message?: string;
        };
        const data = e?.error?.data;
        const status = data?.statusCode ?? e?.error?.statusCode;
        const backendMsg = Array.isArray(data?.message)
            ? data?.message.join(', ')
            : data?.message;
        const message =
            backendMsg ||
            data?.error ||
            e?.error?.message ||
            e?.message ||
            JSON.stringify(error);
        return { ok: false, message: status ? `[${status}] ${message}` : message };
    }
};

/**
 * Delete a conversation thread
 */
export const deleteAlertThread = async (threadId: string): Promise<boolean> => {
    try {
        await ReparoApi({
            method: 'DELETE',
            url: `/agent/alert-creation/thread/${encodeURIComponent(threadId)}`,

        });
        return true;
    } catch (error) {
        console.error('Error deleting thread:', error);
        return false;
    }
};

// =============================================================================
// ALERT TYPE DISPLAY HELPERS
// =============================================================================

export const ALERT_TYPE_LABELS: Record<string, string> = {
    'PO_CREATED': 'New Purchase Order',
    'SKU_UNDERSTOCK': 'Low Stock',
    'SKU_OVERSTOCK': 'Over Stock',
    'SKU_SALES_DRIFT': 'Forecast Variance',
    'DYNAMIC': 'Custom Alert',
};

export const getAlertTypeLabel = (alertType: string): string => {
    return ALERT_TYPE_LABELS[alertType] || alertType;
};
